import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { requiresReportingDraftDiscardConfirmation } from 'src/agent/reporting/conversation/reportingDraft.reducer'
import { useReportingArtifactPinning } from 'src/agent/reporting/conversation/useReportingArtifactPinning'
import { useReportingConversationAccess } from 'src/agent/reporting/conversation/useReportingConversationAccess'
import { useReportingConversationArtifacts } from 'src/agent/reporting/conversation/useReportingConversationArtifacts'
import { useReportingDraft } from 'src/agent/reporting/conversation/useReportingDraft'
import { useReportingRunStream } from 'src/agent/reporting/conversation/useReportingRunStream'
import { reportingAgentQueries } from 'src/queries/reportingAgent/reportingAgent.queries'

import {
  isReportingAccessDenied,
  reportingErrorStatus,
} from './reportingConversation.errors'
import {
  latestRestoredChartContext,
  matchingArtifactRunId,
  reportingConversationStatus,
  reportingStatusMessage,
  toReportingChatMessages,
  toReportingPresentationArtifact,
} from './reportingConversation.presentation'
import type {
  PendingOperation,
  UseReportingAgentConversationOptions,
} from './reportingConversation.types'
import { useReportingConversationSubmission } from './useReportingConversationSubmission'

export type {
  ReportingAgentConversationController,
  ReportingAgentConversationStatus,
  UseReportingAgentConversationOptions,
} from './reportingConversation.types'

/**
 * Routing-free Reporting conversation lifecycle facade. Focused modules own
 * protocol errors, deterministic state, access cleanup, and presentation.
 */
export const useReportingAgentConversation = ({
  actorId,
  organizationId,
  conversationId,
  draftRequestId,
  isAuthenticated,
  onConversationAccepted,
  onAccessDenied,
}: UseReportingAgentConversationOptions) => {
  const queryClient = useQueryClient()
  const draft = useReportingDraft({
    draftRequestId,
  })
  const {
    dispatch: dispatchDraft,
    getStreamSequence,
    startStream,
    stopStream,
    stream,
    resetPrivateState: resetDraftPrivateState,
  } = draft
  const draftState = draft.state
  const draftStateRef = useRef(draftState)
  useEffect(() => {
    draftStateRef.current = draftState
  }, [draftState])
  const foregroundRunIdRef = useRef<string | null>(null)
  const pendingOperationRef = useRef<PendingOperation | null>(null)
  const acceptedConversationRef = useRef<string | null>(null)
  const onConversationAcceptedRef = useRef(onConversationAccepted)

  useEffect(() => {
    onConversationAcceptedRef.current = onConversationAccepted
  }, [onConversationAccepted])

  const draftRequestQuery = useQuery(
    reportingAgentQueries.draftRequest({
      actorId,
      organizationId,
      draftRequestId,
      reportingEnabled: Boolean(
        isAuthenticated && draftRequestId && !conversationId,
      ),
    }),
  )
  const draftRequestFailure = draftRequestQuery.isError
    ? draftRequestQuery.error
    : undefined

  useEffect(() => {
    const submission = draftRequestQuery.data
    if (
      !submission ||
      conversationId ||
      acceptedConversationRef.current === submission.conversation_id
    ) {
      return
    }
    acceptedConversationRef.current = submission.conversation_id
    dispatchDraft({
      type: 'submissionAccepted',
      conversationId: submission.conversation_id,
      messageId: submission.message_id,
      runId: submission.run_id,
      status: submission.status,
    })
    onConversationAcceptedRef.current(submission.conversation_id)
  }, [conversationId, dispatchDraft, draftRequestQuery.data])

  const activeConversationId =
    conversationId ?? draftState.conversationId ?? undefined
  const messagesQuery = useInfiniteQuery(
    reportingAgentQueries.messages({
      actorId,
      organizationId,
      conversationId: activeConversationId,
      reportingEnabled: isAuthenticated,
    }),
  )
  const canonicalMessages = useMemo(
    () => messagesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [messagesQuery.data?.pages],
  )
  const {
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasOlderMessages,
    isFetchingNextPage: isLoadingOlderMessages,
  } = messagesQuery
  const canonicalRunId: string | undefined =
    canonicalMessages.find((message) => message.run_id)?.run_id || undefined
  const queryRunId =
    foregroundRunIdRef.current ??
    canonicalRunId ??
    (draftState.runId?.startsWith('pending-')
      ? undefined
      : (draftState.runId ?? undefined))
  const runQuery = useQuery(
    reportingAgentQueries.run({
      actorId,
      organizationId,
      runId: queryRunId,
      reportingEnabled: isAuthenticated,
    }),
  )
  const draftDisplayQuery = useQuery(
    reportingAgentQueries.draftDisplay({
      actorId,
      organizationId,
      conversationId: activeConversationId,
      artifactId:
        runQuery.data?.artifact_id ?? draftState.artifact?.artifact_id,
      reportingEnabled: isAuthenticated,
    }),
  )
  const clearLocalConversationState = useCallback(() => {
    foregroundRunIdRef.current = null
    pendingOperationRef.current = null
    acceptedConversationRef.current = null
  }, [])

  const resetPrivateState = useCallback(() => {
    clearLocalConversationState()
    resetDraftPrivateState()
  }, [clearLocalConversationState, resetDraftPrivateState])

  const { accessDeniedStatus, accessDeniedRef, denyAccess } =
    useReportingConversationAccess({
      actorId,
      organizationId,
      isAuthenticated,
      queryClient,
      resetPrivateState,
      onAccessDenied,
    })
  const {
    pinArtifact,
    canPinArtifacts,
    pinnedArtifactIds,
    pinningArtifactId,
    pinUnavailableArtifactIds,
  } = useReportingArtifactPinning({
    actorId,
    organizationId,
    isAuthenticated,
    accessDenied: Boolean(accessDeniedStatus),
    accessDeniedRef,
    denyAccess,
  })

  const deniedQueryError = [
    draftRequestFailure,
    messagesQuery.isError ? messagesQuery.error : undefined,
    runQuery.isError ? runQuery.error : undefined,
    draftDisplayQuery.isError ? draftDisplayQuery.error : undefined,
  ].find((error) => isReportingAccessDenied(error))

  useEffect(() => {
    if (deniedQueryError) {
      denyAccess(reportingErrorStatus(deniedQueryError) ?? 403)
    }
  }, [deniedQueryError, denyAccess])

  useEffect(() => {
    if (
      foregroundRunIdRef.current &&
      canonicalMessages.some(
        (message) => message.run_id === foregroundRunIdRef.current,
      )
    ) {
      foregroundRunIdRef.current = null
    }
  }, [canonicalMessages])

  useEffect(() => {
    const waitingForRequiredDisplay =
      runQuery.data?.status === 'completed' &&
      Boolean(runQuery.data.artifact_id) &&
      (draftDisplayQuery.isLoading || draftDisplayQuery.isPending)
    if (
      !activeConversationId ||
      !messagesQuery.data ||
      accessDeniedRef.current ||
      waitingForRequiredDisplay
    )
      return
    const currentDraftState = draftStateRef.current
    dispatchDraft({
      type: 'canonicalReconciled',
      conversationId: activeConversationId,
      messages: canonicalMessages,
      run:
        foregroundRunIdRef.current &&
        currentDraftState.run?.id === foregroundRunIdRef.current
          ? currentDraftState.run
          : (runQuery.data ?? currentDraftState.run),
      artifact: draftDisplayQuery.data ?? currentDraftState.artifact,
    })
  }, [
    activeConversationId,
    canonicalMessages,
    dispatchDraft,
    draftDisplayQuery.data,
    draftDisplayQuery.isLoading,
    draftDisplayQuery.isPending,
    messagesQuery.data,
    runQuery.data,
  ])

  useReportingRunStream({
    activeConversationId,
    actorId,
    organizationId,
    isAuthenticated,
    accessDeniedRef,
    queryClient,
    run: runQuery.data,
    refetchRun: runQuery.refetch,
    refetchMessages: messagesQuery.refetch,
    dispatch: dispatchDraft,
    startStream,
    stopStream,
    getStreamSequence,
    stream,
    denyAccess,
  })
  const submitConversation = useReportingConversationSubmission({
    actorId,
    organizationId,
    conversationId,
    isAuthenticated,
    draftState,
    draftStateRef,
    foregroundRunIdRef,
    pendingOperationRef,
    acceptedConversationRef,
    onConversationAcceptedRef,
    accessDeniedRef,
    denyAccess,
    dispatchDraft,
    queryClient,
    refetchMessages: messagesQuery.refetch,
  })

  const queryFailure = [
    messagesQuery.isError && !messagesQuery.isFetchNextPageError
      ? messagesQuery.error
      : undefined,
    runQuery.isError ? runQuery.error : undefined,
    draftDisplayQuery.isError ? draftDisplayQuery.error : undefined,
  ].find((error) => !isReportingAccessDenied(error))
  const status = reportingConversationStatus(
    Boolean(accessDeniedStatus),
    Boolean(queryFailure),
    draftState.status,
  )
  const messages = useMemo(
    () =>
      toReportingChatMessages(Boolean(accessDeniedStatus), canonicalMessages),
    [accessDeniedStatus, canonicalMessages],
  )
  const restoredChartContext = useMemo(
    () => latestRestoredChartContext(canonicalMessages),
    [canonicalMessages],
  )
  const activeArtifact = toReportingPresentationArtifact(draftState.artifact)
  const activeArtifactRunId = matchingArtifactRunId(
    runQuery.data,
    draftState.artifact,
  )
  const { artifactByRunId } = useReportingConversationArtifacts({
    actorId,
    organizationId,
    conversationId: activeConversationId,
    messages,
    activeRunId: activeArtifactRunId,
    activeArtifact: activeArtifact ?? undefined,
    reportingEnabled: isAuthenticated && !accessDeniedStatus,
    onAccessDenied: denyAccess,
  })
  const canEdit = Boolean(
    actorId &&
      organizationId &&
      isAuthenticated &&
      !accessDeniedStatus &&
      (draftState.conversationId ??
        conversationId ??
        draftState.draftRequestId),
  )
  const isRecoveringRoutedDraft = Boolean(
    draftRequestId &&
      !conversationId &&
      (draftRequestQuery.isPending || draftRequestQuery.isFetching),
  )
  const canSubmit = Boolean(canEdit && !isRecoveringRoutedDraft)
  const submit = useCallback(
    (
      prompt: string,
      chartContext?: Parameters<typeof submitConversation>[1],
    ) => {
      if (!canSubmit) return
      submitConversation(prompt, chartContext)
    },
    [canSubmit, submitConversation],
  )
  const loadOlderMessages = useCallback(() => {
    if (
      !hasOlderMessages ||
      isLoadingOlderMessages ||
      accessDeniedRef.current
    ) {
      return
    }
    void fetchOlderMessages()
  }, [
    accessDeniedRef,
    fetchOlderMessages,
    hasOlderMessages,
    isLoadingOlderMessages,
  ])

  return {
    messages,
    restoredChartContext,
    artifactByRunId,
    status,
    statusMessage: reportingStatusMessage(status),
    errorMessage:
      accessDeniedStatus || queryFailure
        ? undefined
        : draftState.error?.message,
    canEdit,
    canSubmit,
    submit,
    hasOlderMessages: Boolean(hasOlderMessages),
    isLoadingOlderMessages,
    olderMessagesFailed: messagesQuery.isFetchNextPageError,
    loadOlderMessages,
    pinArtifact,
    canPinArtifacts,
    pinnedArtifactIds,
    pinningArtifactId,
    pinUnavailableArtifactIds,
    hasUnsavedWork: requiresReportingDraftDiscardConfirmation(draftState),
  }
}
